import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './SignUp.css';
import logo from '../../images/logo.jpg';
import { FaUser, FaEnvelope, FaPhoneAlt, FaLock, FaArrowRight, FaEye, FaEyeSlash } from 'react-icons/fa';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithPopup, updateProfile } from 'firebase/auth';
import { auth, db, googleProvider } from '../../firebase';

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#FFC107" d="M43.611,20.083h-1.611V20H24v8h11.3c-1.649,4.657-6.08,8-11.3,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C32.701,6.053,28.561,4,24,4C12.955,4,4,12.955,4,24 s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.191,16.104,18.71,13,24,13c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C32.701,6.053,28.561,4,24,4C16.318,4,9.656,8.386,6.306,14.691z" />
    <path fill="#4CAF50" d="M24,44c5.171,0,9.86-1.977,13.409-5.197l-6.199-5.214 C29.702,35.091,26.955,36,24,36c-5.199,0-9.616-3.317-11.277-7.958l-6.543,5.037C9.455,39.556,16.138,44,24,44z" />
    <path fill="#1976D2" d="M43.611,20.083h-1.611V20H24v8h11.3c-0.792,2.237-2.235,4.151-4.09,5.589l0.002-0.001l6.199,5.214 C35.13,39.541,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
  </svg>
);

const SignUp = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile: '',
        password: '',
        confirmPassword: '',
    });
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleGoogleSignup = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;

            localStorage.setItem('loginTimestamp', Date.now().toString());

            const usersRef = collection(db, "users");
            const q = query(usersRef, where("email", "==", user.email));
            const userSnapshot = await getDocs(q);

            if (userSnapshot.empty) {
                await addDoc(usersRef, {
                    uid: user.uid,
                    name: user.displayName || 'Google User',
                    email: user.email,
                    mobile: user.phoneNumber || '',
                    signupMethod: 'google',
                    createdAt: new Date(),
                    role: 'user'
                });
            }

            if (user.displayName) {
                localStorage.setItem('userName', user.displayName);
            }

            if (user.email === "admin@bappatravels.com") {
                localStorage.setItem("isAdminLoggedIn", "true");
                localStorage.setItem("adminEmail", user.email);
                navigate("/admin/dashboard");
            } else {
                navigate("/");
            }
        } catch (error) {
            console.error("Google sign up error:", error);
            setError('Google sign up failed. Please try again.');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        try {
            // Optional pre-check for existing email/mobile
            try {
                const usersRef = collection(db, 'users');
                const emailQuery = query(usersRef, where('email', '==', formData.email));
                const mobileQuery = query(usersRef, where('mobile', '==', formData.mobile));
                const [emailSnap, mobileSnap] = await Promise.all([
                    getDocs(emailQuery),
                    getDocs(mobileQuery)
                ]);
                if (!emailSnap.empty) {
                    setError('This email is already registered. Please log in or use a different email.');
                    return;
                }
                if (!mobileSnap.empty) {
                    setError('This mobile number is already registered. Please log in or use a different number.');
                    return;
                }
            } catch (permissionErr) {
                // If pre-check encounters permission restriction, proceed to Auth validation
                console.warn('Pre-check skipped:', permissionErr);
            }

            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
            try {
                await updateProfile(userCredential.user, { displayName: formData.name });
            } catch (profErr) {
                console.warn('Could not update profile displayName:', profErr);
            }
            try {
                await addDoc(collection(db, 'users'), {
                    uid: userCredential.user.uid,
                    email: formData.email,
                    name: formData.name,
                    mobile: formData.mobile,
                    loginTime: new Date(),
                    createdAt: new Date(),
                    signupMethod: 'manual',
                    role: 'user', // Default role for new users
                });
            } catch (docErr) {
                console.warn('Could not save user profile doc immediately:', docErr);
            }

            setError('');
            navigate('/login');
        } catch (err) {
            if (err.code === 'auth/email-already-in-use') {
                setError('This email is already registered. Please log in or use a different email.');
            } else if (err.code === 'auth/weak-password') {
                setError('Password is too weak. Please use at least 6 characters.');
            } else if (err.code === 'auth/invalid-email') {
                setError('Please enter a valid email address.');
            } else {
                setError('Failed to register user. Please try again.');
            }
            console.error('Registration error:', err);
        }
    };

    return (
        <div className="signup-page-container">
            <div className="signup-container">
                <div className="signup-info-panel">
                    <div className="signup-info-content">
                        <img src={logo} alt="Bappa Taxi Services Logo" className="signup-page-logo" />
                        <h2>Bappa Taxi Services</h2>
                        <p>Create an account to get started.</p>
                    </div>
                </div>
                <div className="signup-form-panel">
                    <div className="signup-form-box">
                        <h2>Sign Up</h2>
                        {error && <p className="error-message" style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <div className="input-row">
                                    <FaUser className="input-icon" />
                                    <input type="text" name="name" placeholder="Full Name" value={formData.name} onChange={handleChange} required />
                                </div>
                            </div>
                            <div className="form-group">
                                <div className="input-row">
                                    <FaEnvelope className="input-icon" />
                                    <input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleChange} required />
                                </div>
                            </div>
                            <div className="form-group">
                                <div className="input-row">
                                    <FaPhoneAlt className="input-icon" />
                                    <input type="tel" name="mobile" placeholder="Mobile Number" value={formData.mobile} onChange={handleChange} required />
                                </div>
                            </div>
                            <div className="form-group">
                                <div className="input-row input-password-wrapper">
                                    <FaLock className="input-icon" />
                                    <input type={showPassword ? "text" : "password"} name="password" placeholder="Password" value={formData.password} onChange={handleChange} required />
                                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <div className="input-row input-password-wrapper">
                                    <FaLock className="input-icon" />
                                    <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" placeholder="Confirm Password" value={formData.confirmPassword} onChange={handleChange} required />
                                    <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                            </div>
                            <button type="submit" className="signup-button">
                                <FaArrowRight className="signup-button-icon" /> Sign Up
                            </button>
                        </form>
                        <button onClick={handleGoogleSignup} className="signup-button google-signin-button" type="button">
                            <span className="google-icon-wrapper"><GoogleG /></span>
                            <span className="google-btn-text">Sign up with Google</span>
                        </button>
                        <p className="login-text">
                            Already have an account? <Link to="/login">Login</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignUp; 